import { Button, Checkbox, ChipConfirmModal, Input, Label, NativeSelect } from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type TrafficSetup as Setup } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { siteSetup } from "~/modules/analytics/pages/tracking/shared";

export function TrafficSetup({ entityId }: { entityId: string }) {
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "traffic", "setup"],
		queryFn: () => api.trafficSetup(entityId),
		...read,
	});

	return <Async state={state}>{(setup) => <Form entityId={entityId} setup={setup} />}</Async>;
}

function Form({ entityId, setup }: { entityId: string; setup: Setup }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const queryClient = useQueryClient();
	const [property, setProperty] = useState(setup.ga_property_id ?? "");
	const [account, setAccount] = useState(setup.ga_credential_id ?? setup.ga_accounts[0]?.id ?? "");
	// What the select is showing, which is not what is saved until a switch is
	// confirmed. Without it the GA fields only appeared once GA was already the
	// source — and switching to GA needs a property id, so a brand that had
	// never used GA could not reach the field that would let it.
	const [choice, setChoice] = useState<"site" | "ga4">(setup.source ?? "site");
	const [params] = useSearchParams();
	const [switching, setSwitching] = useState<"site" | "ga4" | null>(null);

	const save = useMutation({
		mutationFn: (body: Parameters<typeof api.saveTrafficSetup>[1]) => api.saveTrafficSetup(entityId, body),
		onSuccess: (updated) => {
			setChoice(updated.source ?? "site");
			void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
			toast.success(t("traffic.saved"));
		},
		onError: (error: unknown) => toast.error(error instanceof ApiError ? error.localized(locale) : String(error)),
	});

	const connect = useMutation({
		mutationFn: () => api.connectCredential(entityId, "google-analytics", `${location.origin}${location.pathname}`),
		onSuccess: ({ url }) => {
			window.location.assign(url);
		},
	});
	const error = connect.error ?? save.error;

	const snippet = `<script defer src="${setup.script_url}" data-token="${setup.token ?? ""}"${
		setup.cookie ? ' data-cookie="true"' : ""
	}></script>`;

	async function copy(value: string) {
		try {
			await navigator.clipboard.writeText(value);
			toast.success(t("badge.copied"));
		} catch {
			toast.error(t("traffic.copyFailed"));
		}
	}

	return (
		// One fieldset rather than a disabled prop per control: a viewer should
		// find the whole form inert, and a control inside it cannot re-enable
		// itself — which is why every explanation here is a span rather than a
		// hint button.
		<fieldset
			disabled={!editor || save.isPending || connect.isPending}
			title={whyDisabled(editor, why.editor)}
			className="stack bare-fieldset"
		>
			<div className="field">
				<Label htmlFor="traffic-source">{t("traffic.source")}</Label>
				<NativeSelect
					id="traffic-source"
					value={choice}
					onChange={(event) => {
						const next = event.target.value as "site" | "ga4";
						setChoice(next);
						// GA needs a property id alongside the switch, so choosing
						// it only reveals the field; the switch is confirmed from
						// the button beside that.
						if (next === "site" && next !== (setup.source ?? "site")) setSwitching("site");
					}}
				>
					<option value="site">{t("traffic.source.site")}</option>
					<option value="ga4">{t("traffic.source.ga4")}</option>
				</NativeSelect>
				<span className="hint">{t("traffic.sourceHint")}</span>
			</div>

			{choice === "ga4" ? (
				<>
					<div className="field">
						<Label htmlFor="traffic-ga-account">{t("traffic.gaAccount")}</Label>
						<div className="row">
							{setup.ga_accounts.length > 0 ? (
								<NativeSelect
									id="traffic-ga-account"
									value={account}
									onChange={(event) => setAccount(event.target.value)}
								>
									{setup.ga_accounts.map((a) => (
										<option key={a.id} value={a.id}>
											{a.name}
										</option>
									))}
								</NativeSelect>
							) : null}
							<Button
								type="button"
								variant="default"
								disabled={!setup.ga_oauth_configured}
								onClick={() => connect.mutate()}
							>
								{t("traffic.gaAuthorize")}
							</Button>
						</div>
					</div>
					<div className="field">
						<Label htmlFor="traffic-ga">{t("traffic.gaProperty")}</Label>
						<div className="row">
							<Input
								id="traffic-ga"
								value={property}
								placeholder="123456789"
								inputMode="numeric"
								onChange={(event) => setProperty(event.target.value)}
							/>
							<Button
								type="button"
								variant="default"
								disabled={
									!property.trim() ||
									!account ||
									(setup.source === "ga4" &&
										setup.ga_authorized &&
										property === setup.ga_property_id &&
										account === setup.ga_credential_id)
								}
								onClick={() =>
									save.mutate({ source: "ga4", ga_property_id: property.trim(), ga_credential_id: account })
								}
							>
								{t("traffic.gaUse")}
							</Button>
						</div>
					</div>
					<p className="hint">{t("traffic.gaOAuthHint")}</p>
					<p className="muted">{t("traffic.gaLimit")}</p>
					{!setup.ga_oauth_configured ? <p role="alert">{t("traffic.gaUnconfigured")}</p> : null}
					{setup.source === "ga4" && !setup.ga_authorized ? <p role="alert">{t("traffic.gaReauthorize")}</p> : null}
					{setup.ga_last_synced_at ? (
						<p className="faint">
							{t("traffic.gaSynced", { when: fmt.relative(locale, setup.ga_last_synced_at) ?? "" })}
						</p>
					) : null}
				</>
			) : null}

			{choice === "site" && setup.source !== "ga4" ? (
				<>
					{setup.token ? (
						<>
							<h2>{t("traffic.installStep")}</h2>
							<p className="muted">{t("traffic.snippetHint")}</p>
							<pre className="mono whitespace-pre-wrap break-all">{snippet}</pre>
							<div className="row-actions">
								<Button type="button" variant="outline" onClick={() => copy(snippet)}>
									{t("badge.copy")}
								</Button>
							</div>
							<h2>{t("traffic.verifyStep")}</h2>
							<p className="muted">{t("traffic.verifyBody")}</p>
							<div className="row-actions">
								<Button asChild variant="primary">
									<Link to={siteSetup(entityId, "check")}>{t("traffic.start.verify")}</Link>
								</Button>
							</div>
						</>
					) : (
						<Button type="button" onClick={() => save.mutate({ source: "site", rotate_token: true })}>
							{t("traffic.mintToken")}
						</Button>
					)}

					<details>
						<summary className="cursor-pointer">{t("traffic.privacy")}</summary>
						<div className="stack mt-(--s-6)">
							<div className="field">
								<div className="row">
									<Checkbox
										id="traffic-cookie"
										checked={setup.cookie}
										onCheckedChange={(checked) => save.mutate({ cookie: checked === true })}
									/>
									<Label htmlFor="traffic-cookie">{t("traffic.cookie")}</Label>
								</div>
								<span className="hint">{t("traffic.cookieHint")}</span>
							</div>

							<div className="field">
								<Label htmlFor="traffic-retention">{t("traffic.retention")}</Label>
								<NativeSelect
									id="traffic-retention"
									value={String(setup.retention_days)}
									onChange={(event) => save.mutate({ retention_days: Number(event.target.value) })}
								>
									{[30, 90, 180, 365].map((days) => (
										<option key={days} value={days}>
											{t("traffic.retentionDays", { days: String(days) })}
										</option>
									))}
								</NativeSelect>
								<span className="hint">{t("traffic.retentionHint")}</span>
							</div>
						</div>
					</details>
				</>
			) : null}

			{error ? <p role="alert">{error instanceof ApiError ? error.localized(locale) : String(error)}</p> : null}
			{params.has("error") ? <p role="alert">{t("traffic.gaAuthorizationFailed")}</p> : null}
			<ChipConfirmModal
				open={switching !== null}
				onOpenChange={(open) => {
					if (!open) {
						setSwitching(null);
						setChoice(setup.source ?? "site");
					}
				}}
				title={t("traffic.switchTitle")}
				text={t("traffic.switchBody", { source: switching ? t(`traffic.source.${switching}`) : "" })}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("traffic.switchConfirm"),
					onClick: () => {
						if (switching) {
							save.mutate({ source: switching, ga_property_id: property || undefined });
						}
						setSwitching(null);
					},
				}}
			/>
		</fieldset>
	);
}
